import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { reassignTask } from "../../services/newdaily";
import MainButton from "../../components/MainButton/MainButton"
import styles from "./ReassignModal.module.css";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import "overlayscrollbars/overlayscrollbars.css";
import ReassignModalIcon from "../../assets/svgs/reassignModalIcon.svg";
import SearchIcon from "../../assets/svgs/search.svg";
import CloseIcon from "../../assets/svgs/close.svg";
import UserCard from "../../components/UserCard/UserCard";
import departmentIcon from "../../assets/svgs/department.svg";
import departmentIconHover from "../../assets/svgs/departmentHover.svg";
import departmentIconSelected from "../../assets/svgs/departmentSelected.svg";
import departmentIconDisabled from "../../assets/svgs/departmentDisabled.svg";
import arrowDown from "../../assets/svgs/grey_menu_arrow.svg";
import arrowDownHover from "../../assets/svgs/arrow-down-hover.svg";
import arrowDownSelected from "../../assets/svgs/arrow-down-selected.svg";
import checkmark from "../../assets/svgs/checkmark.svg";
import SwitchOrderDefault from "../../assets/svgs/SwitchOrderDefault.svg";
import SwitchOrderHover from "../../assets/svgs/SwitchOrderHover.svg";
import SwitchOrderFocus from "../../assets/svgs/SwitchOrderFocus.svg";

const ReassignModal = ({
    isOpen,
    onClose,
    task,
    allUsers = [],
    allTasks = [],
    selectedDate,
    isMobile,
    onSuccess,
    empleado,
}) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedReassignUserId, setSelectedReassignUserId] = useState(null);
    const [showDeptDropdown, setShowDeptDropdown] = useState(false);
    const [isDeptHovered, setIsDeptHovered] = useState(false);
    const [selectedDepartment, setSelectedDepartment] = useState("");
    const [isAscendingOrder, setIsAscendingOrder] = useState(true);
    const [isSwitchHovered, setIsSwitchHovered] = useState(false);
    const [isSwitchPressed, setIsSwitchPressed] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const deptDropdownRef = useRef(null);

    const allDepartments = ["Comunicación", "Informática", "Diseño", "Administración"];
    const editableTaskLabels = [
        "Gestión interna",
        "Atención al cliente",
        "Propuestas",
        "Formación",
        "Otra",
    ];

    const getUserDepartments = (user) => {
        const raw = user?.Departamentos ?? user?.Departamento ?? "";

        if (Array.isArray(raw)) {
            return raw.map((d) => String(d).trim()).filter(Boolean);
        }

        if (typeof raw === "string") {
            return raw
                .split(",")
                .map((d) => d.trim())
                .filter(Boolean);
        }

        return [];
    };

    const getFirstDepartment = (user) => {
        return getUserDepartments(user)[0] || "";
    };

    const owner = useMemo(() => {
        return allTasks.find((item) =>
            item.tasks.some((t) => String(t.id) === String(task?.id))
        )?.user;
    }, [allTasks, task?.id]);

    const ownerDepartment = useMemo(() => {
        return getFirstDepartment(owner);
    }, [owner]);

    const ownerDepartments = useMemo(() => {
        return getUserDepartments(owner);
    }, [owner]);

    const canChangeDepartment = useMemo(() => {
        const taskName = String(task?.Tarea || "").trim();
        const isEditableTask = editableTaskLabels.includes(taskName);
        const hasMultipleDepartments = ownerDepartments.length > 1;

        return isEditableTask || hasMultipleDepartments;
    }, [task?.Tarea, ownerDepartments]);

    const normalize = (str) =>
        (str || "")
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase();

    const handleSelectDepartment = (dept) => {
        if (!canChangeDepartment) return;

        setSelectedDepartment((prev) => (prev === dept ? "" : dept));
        setShowDeptDropdown(false);
    };

    // useEffect(() => {
    //     if (!isOpen) return;
    //     console.log("🧩 TASK RECIBIDA EN REASSIGN MODAL →", task);
    //     console.log("🧩 task.is_projection →", task?.is_projection);
    //     console.log("🧩 task.Fecha / task.date →", task?.Fecha, task?.date);
    // }, [isOpen, task]);

    useEffect(() => {
        if (!isOpen) return;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "";
        };
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (deptDropdownRef.current && !deptDropdownRef.current.contains(event.target)) {
                setShowDeptDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (!showDeptDropdown) {
            setIsDeptHovered(false);
        }
    }, [showDeptDropdown]);

    useEffect(() => {
        if (!isOpen) return;

        setSearchTerm("");
        setSelectedReassignUserId(null);
        setSelectedDepartment(ownerDepartment || "");
        setIsAscendingOrder(true);
        setIsSwitchHovered(false);
        setIsSwitchPressed(false);
        setShowDeptDropdown(false);
    }, [isOpen, ownerDepartment]);

    const durationMinutes = task?.Minutos ?? 0;
    const durationHours = Math.floor(durationMinutes / 60);
    const durationText = durationHours
        ? `${durationHours}h ${durationMinutes % 60 ? `${durationMinutes % 60}min` : ""}`
        : `${durationMinutes} min`;

    const startTime = task?.Inicio ? moment(task.Inicio, "HH:mm:ss") : null;
    const endTime = startTime ? moment(startTime).add(durationMinutes, "minutes") : null;
    const timeRange = startTime ? `${startTime.format("HH:mm")} - ${endTime.format("HH:mm")}` : "";

    const filteredSortedUsers = useMemo(() => {
        return [...allUsers]
            .filter((u) => u.id !== owner?.id)
            .filter((u) => Number(u.TurnoReal) !== 3)
            .filter((u) => normalize(u.Nombre).includes(normalize(searchTerm.trim())))
            .filter((u) => {
                if (!selectedDepartment) return true;

                const departments = getUserDepartments(u);
                return departments.includes(selectedDepartment);
            })
            .sort((a, b) => {
                const userDataA = allTasks.find((item) => item.user.id === a.id);
                const totalA = userDataA ? userDataA.tasks.reduce((sum, t) => sum + (t.Minutos || 0), 0) : 0;

                const userDataB = allTasks.find((item) => item.user.id === b.id);
                const totalB = userDataB ? userDataB.tasks.reduce((sum, t) => sum + (t.Minutos || 0), 0) : 0;

                if (totalA === totalB) return a.Nombre.localeCompare(b.Nombre);

                return isAscendingOrder ? totalA - totalB : totalB - totalA;
            });
    }, [allUsers, allTasks, owner?.id, searchTerm, selectedDepartment, isAscendingOrder]);

    const handleSelectUserForReassign = (user) => {
        setSelectedReassignUserId(prev => (prev === user.id ? null : user.id));
    };

    const handleFinalReassign = async () => {
        if (!selectedReassignUserId || isSubmitting) return;

        setIsSubmitting(true);

        try {
            const taskDate =
                task?.Fecha ||
                task?.date ||
                selectedDate ||
                moment().format("YYYY-MM-DD");

            const payload = {
                taskId: task.id,
                Empleado: selectedReassignUserId,
                admin: Number(empleado),
                ...(task?.is_projection ? { is_projection: true, date: taskDate } : {}),
            };

            console.log("🔁 REASSIGN FINAL POST →", payload);

            const result = await reassignTask(payload);

            if (result?.OK === 1) {
                onSuccess?.("Tarea reasignada correctamente");
            } else {
                onSuccess?.("Error al reasignar la tarea");
            }
        } catch (error) {
            console.error("❌ Error al reasignar la tarea:", error);
            onSuccess?.("Error al reasignar la tarea");
        } finally {
            setIsSubmitting(false);
            onClose?.();
        }
    };

    if (!isOpen) return null;

    const isButtonDisabled = selectedReassignUserId === null || isSubmitting;

    let deptLeftIcon = departmentIcon;
    let deptRightIcon = arrowDown;
    let switchOrderIcon = SwitchOrderDefault;

    if (isSwitchPressed) {
        switchOrderIcon = SwitchOrderFocus;
    } else if (isSwitchHovered) {
        switchOrderIcon = SwitchOrderHover;
    }

    if (!canChangeDepartment) {
        deptLeftIcon = departmentIconDisabled;
        deptRightIcon = arrowDown;
    } else if (showDeptDropdown) {
        deptLeftIcon = departmentIconSelected;
        deptRightIcon = arrowDownSelected;
    } else if (isDeptHovered) {
        deptLeftIcon = departmentIconHover;
        deptRightIcon = arrowDownHover;
    }

    return (
        <div className={styles.reassignModal} onClick={onClose}>
            <div
                className={`${styles.reassignModalContent} ${isMobile ? styles.reassignModalContentMobile : ""}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={isMobile ? styles.modalTitleRowMobile : styles.modalTitleRow}>
                    <h2 className={styles.modalTitle}>Reasignar tarea</h2>

                    <button
                        onClick={onClose}
                        className={styles.modalCloseButton}
                        aria-label="Cerrar"
                    >
                        <img src={CloseIcon} alt="Cerrar" className={styles.modalCloseIcon} />
                    </button>
                </div>

                <p>
                    De:{" "}
                    <span>{owner?.Nombre || "Desconocido"}</span>
                </p>

                <div className={styles.modalHeader}>
                    <div className={styles.modalTaskSummary}>
                        <div
                            className={styles.modalLeftBar}
                            style={{ backgroundColor: task.color || "#ccc" }}
                        />
                        <div className={styles.modalTaskDetails}>
                            <p className={styles.taskTitle} style={{ margin: 0 }}>
                                <span className={styles.task}>{task.Tarea}</span>{" "}
                                <span className={styles.subtask}>{task.Subtarea}</span> para{" "}
                                <span className={styles.project}>{task.Proyecto}</span>
                            </p>

                            <p className={styles.taskTime} style={{ margin: 0 }}>
                                {durationText} {startTime && `| ${timeRange}`}
                            </p>

                            {task.Descripcion?.trim() && !isMobile && (
                                <p className={styles.taskDescription} style={{ margin: 0 }}>
                                    {task.Descripcion}
                                </p>
                            )}

                            {task.Notas?.trim() && (
                                <p className={styles.taskNote} style={{ color: "#00ADC6", margin: 0 }}>
                                    {task.Notas}
                                </p>
                            )}
                        </div>
                    </div>

                    {!isMobile && (
                        <div className={styles.dividerContainer}>
                            <span className={styles.line}></span>
                            <div className={styles.circle}>
                                <img src={ReassignModalIcon} alt="Reassign Icon" className={styles.circleIcon} />
                            </div>
                            <span className={styles.line}></span>
                        </div>
                    )}

                    <div className={styles.modalParaAndFilterRow}>
                        <div className={styles.modalParaColumn}>
                            <p className={styles.modalText}>
                                Para:{" "}
                                <span>
                                    {selectedReassignUserId
                                        ? allUsers.find((u) => u.id === selectedReassignUserId)?.Nombre
                                        : "Sin asignar"}
                                </span>
                            </p>

                            <div className={styles.departmentFilterWrapper} ref={deptDropdownRef}>
                                <button
                                    className={`${styles.orderButton} ${showDeptDropdown ? styles.active : ""} ${!canChangeDepartment ? styles.orderButtonDisabled : ""}`}
                                    onClick={() => {
                                        if (!canChangeDepartment) return;
                                        setShowDeptDropdown((prev) => !prev);
                                    }}
                                    onMouseEnter={() => {
                                        if (!canChangeDepartment) return;
                                        setIsDeptHovered(true);
                                    }}
                                    onMouseLeave={() => {
                                        if (!canChangeDepartment) return;
                                        setIsDeptHovered(false);
                                    }}
                                >
                                    <img src={deptLeftIcon} alt="Departamento" />
                                    <span>{selectedDepartment || "Departamentos"}</span>
                                    <img src={deptRightIcon} alt="Expandir" />
                                </button>

                                {showDeptDropdown && canChangeDepartment && (
                                    <div className={styles.orderDropdownMenu}>
                                        {allDepartments.map((dept) => (
                                            <div
                                                key={dept}
                                                className={`${styles.orderDropdownMenuItem} ${selectedDepartment === dept ? styles.selectedItem : ""}`}
                                                onClick={() => handleSelectDepartment(dept)}
                                            >
                                                <span>{dept}</span>
                                                {selectedDepartment === dept && (
                                                    <img src={checkmark} alt="Seleccionado" className={styles.checkIcon} />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className={styles.filtersActionsRow}>
                            <div className={`${styles.searchInputWrapper} ${isMobile ? styles.searchInputWrapperMobile : ""}`}>
                                <img
                                    src={SearchIcon}
                                    alt="Buscar"
                                    className={`${styles.searchIcon} ${isMobile ? styles.searchIconMobile : ""}`}
                                />

                                <input
                                    type="text"
                                    className={`${styles.searchInput} ${isMobile ? styles.searchInputMobile : ""}`}
                                    placeholder="Buscar usuario..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />

                                {searchTerm && (
                                    <button
                                        type="button"
                                        className={styles.clearButton}
                                        onClick={() => setSearchTerm("")}
                                    >
                                        <img src={CloseIcon} alt="Limpiar búsqueda" className={styles.clearIcon} />
                                    </button>
                                )}
                            </div>

                            <button
                                type="button"
                                className={styles.switchOrderButton}
                                onClick={() => setIsAscendingOrder((prev) => !prev)}
                                onMouseEnter={() => setIsSwitchHovered(true)}
                                onMouseLeave={() => {
                                    setIsSwitchHovered(false);
                                    setIsSwitchPressed(false);
                                }}
                                onMouseDown={() => setIsSwitchPressed(true)}
                                onMouseUp={() => setIsSwitchPressed(false)}
                            >
                                <img
                                    src={switchOrderIcon}
                                    alt={isAscendingOrder ? "Orden ascendente" : "Orden descendente"}
                                    className={`${styles.switchOrderIcon} ${!isAscendingOrder ? styles.switchOrderIconDesc : ""}`}
                                />
                            </button>
                        </div>
                    </div>
                </div>

                <OverlayScrollbarsComponent
                    options={{
                        scrollbars: { autoHide: "scroll", autoHideDelay: 300 },
                    }}
                    style={{ maxHeight: isMobile ? "70dvh" : "56vh", width: "100%" }}
                >
                    <div className={styles.userListContainer}>
                        {filteredSortedUsers.map((u) => {
                            const userData = allTasks.find(item => item.user.id === u.id);
                            const userTasks = userData ? userData.tasks : [];
                            const isSelected = u.id === selectedReassignUserId;

                            return (
                                <UserCard
                                    key={u.id}
                                    user={u}
                                    tasks={userTasks}
                                    selectedDate={selectedDate}
                                    onSelectUser={handleSelectUserForReassign}
                                    isSelected={isSelected}
                                    admin={true}
                                    modal={true}
                                    isMobile={isMobile}
                                    isModal={true}
                                    isReassignModal={true}
                                />
                            );
                        })}
                    </div>
                </OverlayScrollbarsComponent>

                <div className={styles.footerButtons}>
                    <MainButton
                        type="button"
                        variant="submit"
                        disabled={isButtonDisabled}
                        fullWidth
                        onClick={handleFinalReassign}
                        className={styles.reassignButtonCustom}
                    >
                        {isSubmitting ? "Reasignando..." : "Reasignar tarea"}
                    </MainButton>
                </div>
            </div>
        </div>
    );
};

export default ReassignModal;
